import { coreTypes } from "@core/core-types.di";
import { HttpFailedRequestError } from "@core/data/infrastructures/http/errors/http-failed-request.error";
import { type Http } from "@core/data/infrastructures/http/http";
import { HttpErrorCodeEnum } from "@core/data/infrastructures/http/http-error-response";
import { FallbackError } from "@core/domain/errors/fallback.error";
import { ValidationError } from "@core/domain/errors/validation.error";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { plainToClass } from "class-transformer";
import { inject, injectable } from "inversify";

import {
    ProjectKanbanBlockDto,
    ProjectKanbanBlockQuery,
    ProjectKanbanBlocksDto,
} from "@project/data/dto/kanban/project-kanban-block.dto";
import { ProjectKanbanBlockMapper } from "@project/data/mappers/project-kanban-block.mapper";
import { ProjectKanbanBlock } from "@project/domain/models/project-kanban-block.model";

const PROJECT_KANBAN_BLOCKS_PATH = "/project_kanban_blocks/";

@injectable()
export class ProjectKanbanBlockDataSource {
    constructor(
        @inject(coreTypes.infrastructure.Http)
        private readonly http: Http,
        @inject(ProjectKanbanBlockMapper)
        private readonly mapper: ProjectKanbanBlockMapper,
    ) {}

    async fetchBy(
        pagination: Pagination,
        filters: { project?: number; phase?: string },
    ): Promise<Either<FallbackError, ProjectKanbanBlock[]>> {
        const query: ProjectKanbanBlockQuery = {
            limit: pagination.pageSize,
            offset: pagination.offset,
        };

        if (filters.project) query.project = filters.project;
        if (filters.phase) query.phase = filters.phase;

        const responseResult = await this.http.get<ProjectKanbanBlocksDto>(
            PROJECT_KANBAN_BLOCKS_PATH,
            { query },
        );

        return responseResult
            .mapLeft(() => new FallbackError())
            .map((response) =>
                response.data.results.mapNotNull((dtoRaw) =>
                    this.mapper.map(
                        plainToClass(ProjectKanbanBlockDto, dtoRaw),
                    ),
                ),
            );
    }

    async create(body: {
        project: number;
        name: string;
        phase?: string;
    }): Promise<Either<ValidationError | FallbackError, ProjectKanbanBlock>> {
        const responseResult = await this.http.post<ProjectKanbanBlockDto>(
            PROJECT_KANBAN_BLOCKS_PATH,
            body,
        );

        return responseResult
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new FallbackError();
            })
            .flatMap((response) => {
                const block = this.mapper.map(
                    plainToClass(ProjectKanbanBlockDto, response.data),
                );

                if (!block) return Either.Left(new FallbackError());

                return Either.Right(block);
            });
    }

    async update(
        id: number,
        body: {
            name?: string;
            phase?: string;
            is_active?: boolean;
        },
    ): Promise<Either<ValidationError | FallbackError, ProjectKanbanBlock>> {
        const responseResult = await this.http.patch<ProjectKanbanBlockDto>(
            `${PROJECT_KANBAN_BLOCKS_PATH}${id}/`,
            body,
        );

        return responseResult
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new FallbackError();
            })
            .flatMap((response) => {
                const block = this.mapper.map(
                    plainToClass(ProjectKanbanBlockDto, response.data),
                );

                if (!block) return Either.Left(new FallbackError());

                return Either.Right(block);
            });
    }

    async delete(id: number): Promise<Either<FallbackError, true>> {
        const responseResult = await this.http.delete(
            `${PROJECT_KANBAN_BLOCKS_PATH}${id}/`,
        );

        return responseResult
            .mapLeft(() => new FallbackError())
            .map(() => true);
    }

    async reorder(payload: {
        project: number;
        ids: number[];
    }): Promise<Either<ValidationError | FallbackError, ProjectKanbanBlock[]>> {
        const responseResult = await this.http.post<ProjectKanbanBlockDto[]>(
            `${PROJECT_KANBAN_BLOCKS_PATH}reorder/`,
            payload,
        );

        return responseResult
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new FallbackError();
            })
            .map((response) =>
                (response.data ?? []).mapNotNull((dtoRaw) =>
                    this.mapper.map(
                        plainToClass(ProjectKanbanBlockDto, dtoRaw),
                    ),
                ),
            );
    }
}
